import { Plus, Trash2 } from 'lucide-react';
import type { TrainingQuestionDef } from '@hcc/types';
import { Button } from '../../ui/Button';
import { SHARED_INPUT_CLASS } from './pageMeta';
import { QuestionCommonFields } from './QuestionCommonFields';
import { genId } from './moduleOps';

type CategorizeQuestion = Extract<TrainingQuestionDef, { type: 'categorize' }>;

/**
 * Categorize editor: define the buckets, then the items and which bucket each
 * belongs in. Removing a bucket clears it from any item that referenced it so
 * no item is left pointing at a deleted category.
 */
export function CategorizeQuestionEditor({
  question,
  onChange,
  graded = true,
}: {
  question: CategorizeQuestion;
  onChange: (next: CategorizeQuestion) => void;
  /** False hides the points box (an unmarked assessment). */
  graded?: boolean;
}) {
  function removeCategory(id: string) {
    onChange({
      ...question,
      categories: question.categories.filter((c) => c.id !== id),
      items: question.items.map((it) => (it.category_id === id ? { ...it, category_id: '' } : it)),
    });
  }

  return (
    <div className="space-y-4">
      <QuestionCommonFields
        prompt={question.prompt}
        points={question.points}
        onPrompt={(prompt) => onChange({ ...question, prompt })}
        onPoints={(points) => onChange({ ...question, points })}
        graded={graded}
      />

      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">Categories (buckets)</span>
        {question.categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-2">
            <input
              value={cat.label}
              onChange={(e) =>
                onChange({
                  ...question,
                  categories: question.categories.map((c) =>
                    c.id === cat.id ? { ...c, label: e.target.value } : c,
                  ),
                })
              }
              placeholder="Category name"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <button
              type="button"
              aria-label="Remove category"
              disabled={question.categories.length <= 2}
              onClick={() => removeCategory(cat.id)}
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...question,
              categories: [...question.categories, { id: genId('cat'), label: '' }],
            })
          }
        >
          Add category
        </Button>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-fg-muted">
          Items to sort, and the category each belongs in
        </span>
        {question.items.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <input
              value={item.label}
              onChange={(e) =>
                onChange({
                  ...question,
                  items: question.items.map((x) =>
                    x.id === item.id ? { ...x, label: e.target.value } : x,
                  ),
                })
              }
              placeholder="Item"
              className={`${SHARED_INPUT_CLASS} mt-0 flex-1`}
            />
            <select
              value={item.category_id}
              onChange={(e) =>
                onChange({
                  ...question,
                  items: question.items.map((x) =>
                    x.id === item.id ? { ...x, category_id: e.target.value } : x,
                  ),
                })
              }
              className={`${SHARED_INPUT_CLASS} mt-0 w-40`}
            >
              <option value="">Choose...</option>
              {question.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label || 'Untitled'}
                </option>
              ))}
            </select>
            <button
              type="button"
              aria-label="Remove item"
              disabled={question.items.length <= 1}
              onClick={() =>
                onChange({ ...question, items: question.items.filter((x) => x.id !== item.id) })
              }
              className="rounded-token-sm p-1.5 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          iconLeft={<Plus className="h-4 w-4" />}
          onClick={() =>
            onChange({
              ...question,
              items: [...question.items, { id: genId('item'), label: '', category_id: '' }],
            })
          }
        >
          Add item
        </Button>
      </div>
    </div>
  );
}
